import { useSelectionManager } from "@prismicio/editor-fields"
import { pluralize } from "@prismicio/editor-support/String"
import {
  Box,
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogContent,
  DialogDescription,
  DialogHeader,
  ErrorBoundary,
  IconButton,
  Text,
  Tooltip,
} from "@prismicio/editor-ui"
import { Suspense, useState } from "react"

import { useHasPublisherPermission } from "../DocumentList.utils"
import { useBulkActionsButton } from "./useBulkActionsButton"

export function DeleteDocumentsButton() {
  return (
    <ErrorBoundary renderError={Fallback}>
      <Suspense fallback={<Fallback />}>
        <DeleteButton />
      </Suspense>
    </ErrorBoundary>
  )
}

function DeleteButton() {
  const [open, setOpen] = useState(false)
  const { disabled, tooltip } = useBulkActionsButton({
    hasPermission: useHasPublisherPermission(),
  })

  return (
    <>
      <Tooltip content={tooltip || "Delete"}>
        <IconButton
          icon="delete"
          onClick={() => setOpen(true)}
          disabled={disabled}
          color="grey"
        />
      </Tooltip>
      <DeleteDocumentsConfirmationDialog open={open} onOpenChange={setOpen} />
    </>
  )
}

interface DeleteDocumentsConfirmationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

function DeleteDocumentsConfirmationDialog(
  props: DeleteDocumentsConfirmationDialogProps,
) {
  const { open, onOpenChange } = props

  const { selectedCount } = useSelectionManager()
  const { action, getSelectedDocuments } = useBulkActionsButton({
    hasPermission: useHasPublisherPermission(),
  })
  const selectedDocuments = open ? getSelectedDocuments() : []

  function onDelete() {
    const items = selectedDocuments.map((document) => ({
      documentId: document.id,
      versionId: document.versions[0]?.version_id ?? "",
      status: document.versions[0]?.status ?? "",
    }))

    action("delete", { items })
    onOpenChange(false)
  }

  const maybePluralDocument = pluralize(selectedCount, "document")

  return (
    <Dialog onOpenChange={onOpenChange} open={open} size="small">
      <DialogHeader
        icon="alert"
        title={`Do you really want to delete the selected ${maybePluralDocument}?`}
      />

      <DialogContent>
        <DialogDescription asChild>
          <Box padding={16} flexDirection="column">
            <Text>
              You are about to delete the selected {maybePluralDocument}:{" "}
            </Text>
            <ul>
              {selectedDocuments.map((document, index) => (
                <li key={index}>
                  <Text variant="emphasized">
                    {document.title} ({document.custom_type_id})
                  </Text>
                </li>
              ))}
            </ul>
          </Box>
        </DialogDescription>
      </DialogContent>

      <DialogActions>
        <DialogCancelButton>No</DialogCancelButton>
        <DialogActionButton onClick={onDelete} color="tomato">
          Yes, I want to delete the selected {maybePluralDocument}
        </DialogActionButton>
      </DialogActions>
    </Dialog>
  )
}

function Fallback() {
  return <IconButton icon="delete" disabled color="grey" />
}
